// Boiler Plate Imports
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import {
  remove as removeNotification,
  add as addNotification,
} from "../../redux-store/notificationsSlice";
import { update as setBreadCrumbs } from "../../redux-store/breadCrumbsSlice";
import { update as updateUserProfile } from "../../redux-store/userSlice";
import API from "../../api";
import colors from "../../theme/colors";

// Conatiner Component Imports
import { Button, Card } from "@mui/material";
import BreadCrumbs from "../../components/Navigation/breadcrumbs";
import ModalComponent from "../../components/Modal";
import ConfirmForm from "../../components/Forms/confirmForm";
import TableHeader from "../../components/Page/tableHeader";
import TableSubHeader from "../../components/Page/tableSubHeader";

// Page Component Imports
import OrganizationUserForm from "../../components/Forms/organizationUsersForm";
import { FormatPhoneNumber } from "../../components/PhoneNumberInput";

import { updateReloadOrganization } from "../../redux-store/uxSlice";
import { useParams } from "react-router-dom";
import {
  ItemOnClickLink,
  TableComponent,
} from "../../components/Table/dataGridPro";

const ModalEditHeaderUserName = styled.span`
  color: ${colors.gray};
  margin-left: 5px;
`;

const Table = React.memo(TableComponent);

const OrganizationUsersContainer = () => {
  const { organizationId } = useParams();

  const userProfile = useSelector((state) => state.user);
  const organization = useSelector((state) => state.organization);

  const [savingUser, setSavingUser] = useState(false);
  const [savingUserError, setSavingUserError] = useState(false);
  const [userTableRows, setUserTableRows] = useState([]);
  const [reloadData, setReloadData] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openDeactivateModal, setOpenDeactivateModal] = useState(false);
  const [openReactivateModal, setOpenReactivateModal] = useState(false);
  const [openEditUserModal, setOpenEditUserModal] = useState(false);
  const [currentUserEdit, setCurrentUserEdit] = useState({});
  const dispatch = useDispatch();

  useEffect(() => {}, []);

  const tableColumns = useMemo(() => {
    return [
      {
        headerName: "Name",
        field: "name",
        valueGetter: (value, row) => {
          return row.name;
        },
        renderCell: ({ row }) => {
          if (userProfile?.role && userProfile?.role !== "TECH") {
            return (
              <ItemOnClickLink onClick={() => handlers.editUserClick(row.id)}>
                {row.name}
              </ItemOnClickLink>
            );
          } else {
            return row.name;
          }
        },
      },
      {
        headerName: "Email Address",
        field: "email",
      },
      {
        headerName: "Phone Number",
        field: "phoneString",
        valueGetter: (value, row) => {
          return row.phone;
        },
      },
      {
        headerName: "Status",
        field: "status",
      },
      {
        headerName: "Role",
        field: "role",
        valueGetter: (value, row) => {
          if (row.type === "NETWORK") {
            return `${row.role} (${row.networkOrganizationPermission})`;
          } else {
            return row.role;
          }
        },
      },
      {
        headerName: "Date Created",
        field: "dateCreateObject",
        type: "dateTime",
      },
    ];
  }, [userProfile?.role, userTableRows]);

  useEffect(() => {
    //Set Bread Crumbs
    dispatch(
      setBreadCrumbs([
        {
          title: organization.name,
          link: "/organization",
        },
        {
          title: "Users",
          link: "/users",
        },
      ])
    );
  }, [organization]);

  const dataLayer = {
    GetOrganizationUsers: async () => {
      const users = await API.User.UserGetOrganizationUsers({ organizationId });
      const userRows = users.map((user) => {
        return {
          ...user,
          name: `${user.firstName} ${user.lastName}`,
          status: (() => {
            if (user.deleted) {
              return "Deactivated";
            } else if (user.invitePending) {
              return "Invite Pending";
            } else {
              return "Active";
            }
          })(),
          dateCreateObject: new Date(user.created),
          phoneString: `${FormatPhoneNumber(user.phone)}`,
        };
      });
      userRows.sort((a, b) => {
        return b.created - a.created;
      });

      setUserTableRows(userRows);
      return userRows;
    },
    GetUserProfile: async () => {
      const userProfile = await API.User.UserGet();
      dispatch(updateUserProfile(userProfile));
    },
  };

  const handlers = {
    addUserClick: () => {
      setSavingUserError("");
      setOpenModal(true);
    },
    editUserClick: (userId) => {
      const user = userTableRows.find((u) => u.id === userId);
      setCurrentUserEdit(user);
      setOpenEditUserModal(true);
    },
    closeUserModal: () => {
      if (!savingUser) {
        setOpenModal(false);
      }
    },
    saveUser: async (data) => {
      setSavingUserError("");
      setSavingUser(true);
      try {
        await API.User.UserCreate({
          createUserData: { ...data, type: "CUSTOMER", organizationId },
        });
        //Reload Users
        setReloadData(true);
        dispatch(updateReloadOrganization(true));
        //Create Success Notification
        setOpenModal(false);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "success",
            title: "Success",
            message: "User created successfully",
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      } catch (err) {
        setSavingUserError(err);
        console.log(err);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message: err,
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      }
      setSavingUser(false);
    },
    saveEditUser: async (data) => {
      setSavingUserError("");
      setSavingUser(true);
      try {
        await API.User.UserUpdate({
          editUserData: { ...currentUserEdit, ...data },
        });
        //Did I just update me?
        if (userProfile.id === currentUserEdit.id) {
          await dataLayer.GetUserProfile();
        }

        //Reload Users
        setReloadData(true);

        //Create Success Notification
        setOpenEditUserModal(false);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "success",
            title: "Success",
            message: "User updated successfully",
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      } catch (err) {
        setSavingUserError(err);
        console.log(err);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message: err,
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      }
      setSavingUser(false);
    },
    closeEditUserModal: () => {
      setOpenEditUserModal(false);
    },
    confirmDeactivateUserModal: () => {
      setOpenDeactivateModal(true);
    },
    deactivateUser: async () => {
      setSavingUserError("");
      setSavingUser(true);
      try {
        await API.User.UserDelete({
          deleteUserId: currentUserEdit.id,
        });
        //Reload Users
        setReloadData(true);
        //Create Success Notification
        setOpenDeactivateModal(false);
        setOpenEditUserModal(false);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "success",
            title: "Success",
            message: "User deactivated successfully",
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      } catch (err) {
        setSavingUserError(err);
        console.log(err);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message: err,
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      }
      setSavingUser(false);
    },
    confirmReactivateUserModal: () => {
      setOpenReactivateModal(true);
    },
    reactivateUser: async () => {
      setSavingUserError("");
      setSavingUser(true);
      try {
        await API.User.UserDelete({
          deleteUserId: currentUserEdit.id,
          reactivate: "true",
        });
        //Reload Users
        setReloadData(true);
        setOpenReactivateModal(false);
        setOpenEditUserModal(false);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "success",
            title: "Success",
            message: "User reactivated successfully",
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      } catch (err) {
        setSavingUserError(err);
        console.log(err);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message: err,
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      }
      setSavingUser(false);
    },
    closeDeactivateModal: () => {
      setOpenDeactivateModal(false);
    },
    closeReactivateModal: () => {
      setOpenReactivateModal(false);
    },
    resendUserInviteEmail: async () => {
      setSavingUserError("");
      setSavingUser(true);
      try {
        await API.User.ResendUserInvite({
          resendUserId: currentUserEdit.id,
        });
        //Reload Users
        setReloadData(true);
        setOpenReactivateModal(false);
        setOpenEditUserModal(false);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "success",
            title: "Success",
            message: "User invite resent. Check email.",
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      } catch (err) {
        setSavingUserError(err);
        console.log(err);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message: err,
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      }
      setSavingUser(false);
    },
  };

  return (
    <>
      
      <Card>
        <TableHeader>
          <div>
            <h2>Users</h2>
            <TableSubHeader>
              Below is a list of all Users within this Organization
            </TableSubHeader>
          </div>
          {userProfile?.role && userProfile.role !== "TECH" && (
            <Button
              onClick={handlers.addUserClick}
              variant="contained"
              color="primary"
              size="large"
            >
              ADD USER
            </Button>
          )}
        </TableHeader>
        <Table
          columns={tableColumns}
          autoResizeColumns={true}
          label={""}
          isAdminView={false}
          reloadData={reloadData}
          placeholder="There are no users created yet"
          getRowsFunction={dataLayer.GetOrganizationUsers}
          parentLoading={true}
          initialState={{
            pinnedColumns: { left: ["name"] },
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
        />
      </Card>
      <ModalComponent
        headerText={`Reactivate ${currentUserEdit.firstName} ${currentUserEdit.lastName}`}
        open={openReactivateModal}
      >
        <ConfirmForm
          handleSave={handlers.reactivateUser}
          handleCancel={handlers.closeReactivateModal}
          loading={savingUser}
          error={savingUserError}
        >
          <div>
            <b>
              Are you sure you want to Reactivate {currentUserEdit.firstName}{" "}
              {currentUserEdit.lastName}?
            </b>
            <p>
              This will enable this user's access to Boreas. They will be able
              to sign in. You can revoke access by deactivating this user.
            </p>
          </div>
        </ConfirmForm>
      </ModalComponent>
      <ModalComponent
        headerText={`Deactivate ${currentUserEdit.firstName} ${currentUserEdit.lastName}`}
        open={openDeactivateModal}
      >
        <ConfirmForm
          handleSave={handlers.deactivateUser}
          handleCancel={handlers.closeDeactivateModal}
          loading={savingUser}
          error={savingUserError}
        >
          <div>
            <b>
              Are you sure you want to Deactivate {currentUserEdit.firstName}{" "}
              {currentUserEdit.lastName}?
            </b>
            <p>
              This will disable this user's access to Boreas. They will be
              unable to sign in. The user will not be deleted. You can restore
              access by reactivating this user.
            </p>
          </div>
        </ConfirmForm>
      </ModalComponent>
      <ModalComponent headerText="Add User" open={openModal}>
        <OrganizationUserForm
          handleSave={handlers.saveUser}
          handleCancel={handlers.closeUserModal}
          loading={savingUser}
          error={savingUserError}
        />
      </ModalComponent>
      <ModalComponent
        headerText={
          <>
            Manage User
            <ModalEditHeaderUserName>
              / {currentUserEdit.name}
            </ModalEditHeaderUserName>
          </>
        }
        open={openEditUserModal}
      >
        <OrganizationUserForm
          user={currentUserEdit}
          handleSave={handlers.saveEditUser}
          handleCancel={handlers.closeEditUserModal}
          loading={savingUser}
          error={savingUserError}
          isEdit={true}
          showDeactivate={
            currentUserEdit.id !== userProfile.id &&
            !(
              currentUserEdit.type === "NETWORK" &&
              userProfile.type === "CUSTOMER"
            )
          }
          handleDeactivateUser={handlers.confirmDeactivateUserModal}
          handleReactivateUser={handlers.confirmReactivateUserModal}
          handleResendInvite={handlers.resendUserInviteEmail}
        />
      </ModalComponent>
    </>
  );
};

export default OrganizationUsersContainer;
